import type { ProjectEquipmentGroupWithAssignments } from "@/hooks/useProjectEquipment";
import type { ProjectWithRelations } from "@/hooks/useProjects";

export type CarnetAtaExportRow = {
  itemNo: number;
  description: string;
  quantity: number;
  totalValue: number | null;
  totalWeight: number | null;
  weightUnit: string;
  countryOfOrigin: string;
};

const headers = [
  "ZAHLENFOLGE",
  "STÜCKZAHL",
  "Handelsübliche Bezeichnung der Waren",
  "Weight",
  "Weight Unit",
  "WERT",
  "URSPRUNGSLAND",
];

function formatEquipmentDescription(
  assignment: ProjectEquipmentGroupWithAssignments["assignments"][number],
) {
  if (assignment.temporary_equipment) {
    return (
      assignment.temporary_equipment.carnet_ata_description ||
      assignment.temporary_equipment.name
    );
  }
  const equipment = assignment.equipment;
  if (!equipment) return "";
  if (equipment.carnet_ata_description) return equipment.carnet_ata_description;
  return [equipment.manufacturer, equipment.name].filter(Boolean).join(" ");
}

export function buildCarnetAtaRows(
  groups: ProjectEquipmentGroupWithAssignments[],
  selectedGroupIds: string[],
): CarnetAtaExportRow[] {
  const selectedGroupIdSet = new Set(selectedGroupIds);
  let itemNo = 1;

  return groups.flatMap((group) => {
    if (!selectedGroupIdSet.has(group.id)) return [];

    return group.assignments.map((assignment) => {
      const equipment = assignment.equipment;
      const temporaryEquipment = assignment.temporary_equipment;
      const ataValues = equipment ?? temporaryEquipment;
      const quantity = assignment.amount;
      const unitValue =
        ataValues?.carnet_ata_value ?? equipment?.new_price ?? null;
      const unitWeight = ataValues?.weight ?? null;

      return {
        itemNo: itemNo++,
        description: formatEquipmentDescription(assignment),
        quantity,
        totalValue: unitValue === null ? null : unitValue * quantity,
        totalWeight: unitWeight === null ? null : unitWeight * quantity,
        weightUnit: ataValues?.carnet_ata_weight_unit || "kg",
        countryOfOrigin: ataValues?.country_of_origin ?? "",
      };
    });
  });
}

function csvCell(value: string | number | null) {
  const text = value === null ? "" : String(value);
  if (!/[";\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function formatNumber(value: number | null) {
  return value === null ? null : Number(value.toFixed(2));
}

export function buildCarnetAtaCsv(rows: CarnetAtaExportRow[]) {
  const csvRows = [
    headers,
    ...rows.map((row) => [
      row.itemNo,
      row.quantity,
      row.description,
      formatNumber(row.totalWeight),
      row.weightUnit || "kg",
      formatNumber(row.totalValue),
      row.countryOfOrigin,
    ]),
  ];

  return csvRows.map((row) => row.map(csvCell).join(";")).join("\r\n");
}

function slugify(value: string) {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "project"
  );
}

export function downloadCarnetAtaCsv(
  project: ProjectWithRelations,
  groups: ProjectEquipmentGroupWithAssignments[],
  selectedGroupIds: string[],
) {
  const rows = buildCarnetAtaRows(groups, selectedGroupIds);
  const csv = buildCarnetAtaCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${slugify(project.name)}-carnet-ata.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
